/**
 * DebugPanel – mobile-first diagnostic console for client-side logs.
 *
 * Features:
 * - Floating pill that only appears when debug mode is enabled (large tap target).
 * - Slide-in panel (bottom sheet on mobile, side on md+) with live updating log viewer.
 * - Toggle to turn debug on/off (persists via localStorage).
 * - Search + multi-select level filters (log/info/warn/error).
 * - Color-coded entries, monospace, timestamps.
 * - Clear, Copy All as JSON, Send to Support (with optional user note textarea).
 * - Uses sendBeacon + visibility listeners under the hood for reliability.
 *
 * Privacy:
 * - Only active when user explicitly enables it.
 * - "Send to Support" is a deliberate user action.
 * - Strong redaction happens in the logger service before anything is buffered.
 *
 * See Privacy Policy: "Telemetry & Diagnostics" and "Provide Customer Support".
 */

import * as React from "react";
import { Bug, Copy, Send, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useClientLogger } from "@/hooks/useClientLogger";
import type { ClientLogEntry, LogLevel } from "@/lib/client-logger";

const LEVELS: LogLevel[] = ["log", "info", "warn", "error"];

const levelColor: Record<LogLevel, string> = {
  log: "text-foreground",
  info: "text-blue-600 dark:text-blue-400",
  warn: "text-amber-600 dark:text-amber-400",
  error: "text-red-600 dark:text-red-400",
  debug: "text-muted-foreground",
};

const levelBadge: Record<LogLevel, string> = {
  log: "bg-muted text-foreground",
  info: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  error: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  debug: "bg-muted text-muted-foreground",
};

export function DebugPanel() {
  const { enabled, logs, setEnabled, clear, sendToSupport } = useClientLogger();

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [activeLevels, setActiveLevels] = React.useState<Set<LogLevel>>(
    () => new Set(LEVELS)
  );
  const [note, setNote] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sentMessage, setSentMessage] = React.useState<string | null>(null);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Filtered + searched view (cheap for 600 items)
  const filteredLogs = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((entry) => {
      if (!activeLevels.has(entry.level as LogLevel)) return false;
      if (!q) return true;
      return (
        entry.message.toLowerCase().includes(q) ||
        (entry.args && JSON.stringify(entry.args).toLowerCase().includes(q))
      );
    });
  }, [logs, search, activeLevels]);

  // Auto-scroll to bottom when new logs arrive and panel is open
  React.useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      // user was near bottom — follow new logs
      el.scrollTop = el.scrollHeight;
    }
  }, [filteredLogs.length, open]);

  const toggleLevel = (level: LogLevel) => {
    const next = new Set(activeLevels);
    if (next.has(level)) {
      if (next.size === 1) return; // keep at least one
      next.delete(level);
    } else {
      next.add(level);
    }
    setActiveLevels(next);
  };

  const handleClear = () => {
    clear();
    setSentMessage(null);
  };

  const handleCopyAll = async () => {
    try {
      const json = JSON.stringify(filteredLogs, null, 2);
      await navigator.clipboard.writeText(json);
      // tiny non-blocking feedback
      const orig = document.title;
      document.title = "Copied debug JSON ✓";
      setTimeout(() => {
        document.title = orig;
      }, 1200);
    } catch {
      // fallback
      const blob = new Blob([JSON.stringify(filteredLogs, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client-debug-${new Date().toISOString().slice(0, 19)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSendToSupport = async () => {
    if (!enabled || filteredLogs.length === 0) return;

    setSending(true);
    setSentMessage(null);

    try {
      // This triggers the real flush with beacon/fetch + the optional note
      sendToSupport(note.trim() || undefined);

      setSentMessage("Sent to support. Thank you!");
      setNote("");

      // Keep the panel open briefly so user sees confirmation
      setTimeout(() => {
        setSentMessage(null);
      }, 2600);
    } finally {
      setSending(false);
    }
  };

  const closePanel = () => {
    setOpen(false);
    setSentMessage(null);
  };

  // Keyboard escape
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!enabled) return null;

  return (
    <>
      {/* Floating pill – only visible when debug enabled. Very touch friendly. */}
      <button
        onClick={() => setOpen(true)}
        className={cn(
          "fixed bottom-4 right-4 z-[70] flex items-center gap-2 rounded-full border bg-background/95 px-4 py-2.5 text-sm font-medium shadow-lg backdrop-blur",
          "active:scale-[0.985] touch-manipulation",
          "hover:bg-accent hover:text-accent-foreground",
          "md:bottom-5 md:right-5"
        )}
        aria-label="Open debug diagnostics panel"
      >
        <Bug className="size-4" />
        <span className="font-mono tabular-nums">{logs.length}</span>
        <span className="hidden sm:inline text-muted-foreground">Debug</span>
      </button>

      {/* Backdrop + Panel */}
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center md:items-center"
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={closePanel}
          />

          {/* Drawer / Panel */}
          <div
            className={cn(
              "relative z-10 w-full max-w-full rounded-t-2xl border bg-background shadow-2xl",
              "md:w-[min(820px,95vw)] md:rounded-2xl md:m-4",
              "flex h-[82dvh] flex-col md:h-[min(620px,82vh)]"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3 md:px-5">
              <div className="flex items-center gap-3">
                <Bug className="size-5 text-muted-foreground" />
                <div>
                  <div className="font-semibold">Client Debug Logs</div>
                  <div className="text-[10px] text-muted-foreground -mt-0.5">
                    {logs.length} captured • local only until sent
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Debug mode</span>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => {
                      setEnabled(v);
                      if (!v) closePanel();
                    }}
                    aria-label="Toggle debug logging"
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={closePanel}>
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2 md:px-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search logs…"
                className="flex-1 min-w-[140px] rounded-md border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />

              <div className="flex items-center gap-1">
                {LEVELS.map((lvl) => {
                  const active = activeLevels.has(lvl);
                  return (
                    <button
                      key={lvl}
                      onClick={() => toggleLevel(lvl)}
                      className={cn(
                        "rounded px-2 py-0.5 text-xs font-medium transition-colors border",
                        active
                          ? levelBadge[lvl]
                          : "bg-background text-muted-foreground border-transparent hover:bg-accent"
                      )}
                    >
                      {lvl}
                    </button>
                  );
                })}
              </div>

              <div className="ml-auto flex gap-1.5">
                <Button variant="outline" size="sm" onClick={handleClear}>
                  <Trash2 className="mr-1.5 size-3.5" />
                  Clear
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyAll}>
                  <Copy className="mr-1.5 size-3.5" />
                  Copy JSON
                </Button>
              </div>
            </div>

            {/* Live log viewer */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-auto bg-black/95 p-2 font-mono text-[11px] leading-tight text-white/90 md:p-3"
            >
              {filteredLogs.length === 0 && (
                <div className="py-10 text-center text-muted-foreground">
                  No matching logs.
                </div>
              )}

              {filteredLogs.map((entry) => (
                <div
                  key={entry.id}
                  className="group mb-px flex gap-2 border-b border-white/10 py-[3px] px-1 hover:bg-white/5"
                >
                  <span className="shrink-0 tabular-nums text-[10px] text-white/50 pt-px w-[82px]">
                    {new Date(entry.ts).toLocaleTimeString([], {
                      hour12: false,
                    })}
                  </span>

                  <span
                    className={cn(
                      "shrink-0 rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider self-start mt-px",
                      levelBadge[entry.level as LogLevel] ||
                        "bg-white/10 text-white"
                    )}
                  >
                    {entry.level}
                  </span>

                  <div className="min-w-0 flex-1 break-words text-white/95">
                    <span>{entry.message}</span>
                    {entry.args && entry.args.length > 0 && (
                      <span className="ml-2 text-white/60">
                        {JSON.stringify(entry.args)}
                      </span>
                    )}
                    {entry.stack && (
                      <pre className="mt-0.5 max-h-[80px] overflow-auto whitespace-pre-wrap text-[10px] text-red-400/80">
                        {entry.stack}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Send to Support section */}
            <div className="border-t bg-background p-3 md:p-4">
              <div className="text-xs text-muted-foreground mb-1.5">
                Send current buffer + optional note to support
              </div>

              <div className="flex flex-col gap-2 md:flex-row">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note for the support team (what were you doing?)"
                  className="min-h-[60px] md:min-h-[42px] flex-1 text-sm"
                  disabled={sending}
                />
                <div className="flex gap-2 md:flex-col md:gap-1.5 shrink-0">
                  <Button
                    onClick={handleSendToSupport}
                    disabled={sending || logs.length === 0}
                    className="w-full md:w-auto"
                  >
                    <Send className="mr-1.5 size-4" />
                    {sending ? "Sending…" : "Send to Support"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNote("");
                      setSentMessage(null);
                    }}
                    disabled={sending}
                    className="md:w-auto"
                  >
                    Clear note
                  </Button>
                </div>
              </div>

              {sentMessage && (
                <div className="mt-2 text-sm text-emerald-600 dark:text-emerald-400">
                  {sentMessage}
                </div>
              )}

              <p className="mt-2 text-[10px] text-muted-foreground">
                Only logs captured while debug mode is on are included. Data is transmitted using
                reliable background mechanisms (sendBeacon) when possible.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
